import { useEffect, useMemo, useState } from 'react';
import { Alert, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Caption, ThemedText } from '@/components/ui/Text';
import type { Trip } from '@/services/trips';
import type { ItineraryItem } from '@/services/itinerary';
import { dismissReminder, getActiveReminders, type SmartReminder } from '@/services/smartReminders';
import { copyToClipboard } from '@/utils/clipboard';

type SmartRemindersPanelProps = {
  trip: Trip;
  itineraryItems: ItineraryItem[];
  collaboratorCount?: number;
  onOpenIdeas?: () => void;
};

const iconFor = (id: SmartReminder['type']): keyof typeof Ionicons.glyphMap => {
  switch (id) {
    case 'hotel_address':
      return 'bed-outline';
    case 'meeting_point':
      return 'people-outline';
    case 'screenshot_plan':
      return 'image-outline';
    case 'backup_indoor':
      return 'rainy-outline';
    default:
      return 'alert-circle-outline';
  }
};

export function SmartRemindersPanel({
  trip,
  itineraryItems,
  collaboratorCount = 0,
  onOpenIdeas,
}: SmartRemindersPanelProps) {
  const theme = useTheme();
  const [reminders, setReminders] = useState<SmartReminder[]>([]);
  const [loading, setLoading] = useState(false);

  const hasHotelAddress = useMemo(() => {
    const hotel = itineraryItems.find((i) => i.category === 'hotel');
    const addr = (hotel?.address || hotel?.location || '').trim();
    return addr || null;
  }, [itineraryItems]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const next = await getActiveReminders(trip, itineraryItems, { collaboratorCount });
        if (!cancelled) setReminders(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [trip.id, trip.startDate, trip.endDate, itineraryItems, collaboratorCount]);

  const dismiss = async (id: string) => {
    try {
      await dismissReminder(trip.id, id);
    } finally {
      setReminders((prev) => prev.filter((r) => r.id !== id));
    }
  };

  const handleAction = async (r: SmartReminder) => {
    if (r.type === 'hotel_address') {
      if (!hasHotelAddress) {
        Alert.alert('No hotel address found', 'Add a hotel item with an address to copy it.');
        return;
      }
      await copyToClipboard(hasHotelAddress);
      Alert.alert('Copied', 'Hotel address copied to clipboard.');
      await dismiss(r.id);
      return;
    }
    if (r.type === 'backup_indoor' && onOpenIdeas) {
      onOpenIdeas();
      await dismiss(r.id);
      return;
    }
    // Default: acknowledge/dismiss
    await dismiss(r.id);
  };

  if (loading && reminders.length === 0) return null;
  if (reminders.length === 0) return null;

  return (
    <Card variant="outlined" style={{ marginBottom: theme.spacing(1.25) }}>
      <View style={{ padding: theme.spacing(1.25), gap: theme.spacing(1) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), flex: 1 }}>
            <Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />
            <ThemedText variant="bodyStrong" style={{ fontSize: 14, fontWeight: '800' }}>
              Smart reminders
            </ThemedText>
          </View>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, fontWeight: '700' }}>
            {reminders.length}
          </Caption>
        </View>

        <View style={{ gap: theme.spacing(0.75) }}>
          {reminders.map((r) => (
            <View
              key={r.id}
              style={{
                padding: theme.spacing(1),
                borderRadius: theme.radii.lg,
                backgroundColor: theme.colors.borderMuted + '12',
                gap: theme.spacing(0.75),
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.75) }}>
                <Ionicons name={iconFor(r.type)} size={18} color={theme.colors.textSecondary} />
                <View style={{ flex: 1, gap: 2 }}>
                  <ThemedText variant="bodyStrong" style={{ fontSize: 13 }}>
                    {r.title}
                  </ThemedText>
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                    {r.description}
                  </Caption>
                </View>
              </View>

              <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                {r.type === 'backup_indoor' && onOpenIdeas ? (
                  <Button
                    label="Browse ideas"
                    variant="secondary"
                    size="small"
                    onPress={() => void handleAction(r)}
                    style={{ flex: 1 }}
                  />
                ) : r.actionLabel ? (
                  <Button
                    label={r.actionLabel}
                    variant="secondary"
                    size="small"
                    onPress={() => void handleAction(r)}
                    style={{ flex: 1 }}
                  />
                ) : (
                  <Pressable
                    onPress={() => void handleAction(r)}
                    style={({ pressed }) => ({
                      flex: 1,
                      minHeight: 36,
                      borderRadius: theme.radii.md,
                      backgroundColor: theme.colors.surface,
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: pressed ? 0.7 : 1,
                    })}
                    accessibilityRole="button"
                    accessibilityLabel="Dismiss reminder"
                  >
                    <Caption style={{ fontWeight: '700', color: theme.colors.textSecondary }}>Dismiss</Caption>
                  </Pressable>
                )}

                <Button
                  label="Hide"
                  variant="ghost"
                  size="small"
                  onPress={() => void dismiss(r.id)}
                  style={{ minWidth: 72 }}
                />
              </View>
            </View>
          ))}
        </View>
      </View>
    </Card>
  );
}

